// frontend/src/config/axiosConfig.js

/*import axios from 'axios';
import { auth } from './firebaseConfig';

const axiosInstance = axios.create({
  baseURL: 'https://backend-box-digital.onrender.com',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Añadir token de Firebase a cada solicitud
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return config;

      const token = await currentUser.getIdToken(true);
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    } catch (error) {
      console.error('Error al obtener token para la solicitud:', error);
      return config;
    }
  },
  (error) => Promise.reject(error)
);

// Manejo de respuestas y reintento en error 401
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error('No hay usuario autenticado');

        const newToken = await currentUser.getIdToken(true);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Error al renovar token:', refreshError);
        // window.location.href = '/login';
      }
    }

    if (error.response) {
      switch (error.response.status) {
        case 404:
          error.customMessage = 'Recurso no encontrado. Verifica la URL de la API.';
          break;
        case 403:
          error.customMessage = 'Acceso prohibido. No tienes permisos para este recurso.';
          break;
        case 500:
        case 502:
        case 503:
          error.customMessage = 'Error en el servidor. Intenta más tarde.';
          break;
        default:
          error.customMessage = error.response.data?.error || `Error ${error.response.status}`;
      }
    } else if (error.request) {
      error.customMessage = 'Sin respuesta del servidor. Verifica tu conexión.';
    } else {
      error.customMessage = `Error de configuración: ${error.message}`;
    }

    console.error('Error de red:', error.customMessage || error.message);
    return Promise.reject(error);
  }
);

export default axiosInstance;*/

// frontend/src/config/axiosConfig.js
import axios from 'axios';
import { auth } from './firebaseConfig';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://backend-box-digital.onrender.com',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Origin sin /api (por si tu base tuviera /api; acá no la tiene)
export const API_ORIGIN = axiosInstance.defaults.baseURL.replace(/\/api\/?$/, "");

// “Despertador” del backend: prueba /health, / y /api/health con backoff 3s/6s/9s
export async function wakeBackend() {
  const urls = ["/health", "/", "/api/health"].map((p) => `${API_ORIGIN}${p}`);
  for (let i = 0; i < 3; i++) {
    for (const url of urls) {
      try {
        const r = await fetch(url, { method: "GET", cache: "no-store" });
        if (r.ok) return true;
      } catch {}
    }
    await new Promise((r) => setTimeout(r, 3000 * (i + 1))); // 3s, 6s, 9s
  }
  return false;
}

// —— Request: adjunta token de Firebase ——
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return config;
      const token = await currentUser.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    } catch (error) {
      console.error('Error al obtener token para la solicitud:', error);
      return config;
    }
  },
  (error) => Promise.reject(error)
);

// —— Response: 401 => renueva token | network/timeout => despierta y reintenta ——
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};

    // 401: intenta renovar token una sola vez
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error('No hay usuario autenticado');
        const newToken = await currentUser.getIdToken(true);
        // Acá SÍ usamos getIdToken(true) porque es un retry forzado tras 401
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error('Error al renovar token:', refreshError);
        // window.location.href = '/login';
      }
    }

    // Sin respuesta del servidor (timeout / network): despierta y reintenta 1 vez
    const isNetworkError = !error.response; // no hubo respuesta
    if (isNetworkError && !originalRequest._wokeRetry) {
      originalRequest._wokeRetry = true; // evita loop
      await wakeBackend();
      try {
        return await axiosInstance(originalRequest);
      } catch (e) {
        error = e; // sigue al manejo estándar
      }
    }

    // Mensajes amigables
    if (error.response) {
      switch (error.response.status) {
        case 404:
          error.customMessage = 'Recurso no encontrado. Verifica la URL de la API.';
          break;
        case 403:
          error.customMessage = 'Acceso prohibido. No tienes permisos para este recurso.';
          break;
        case 500:
        case 502:
        case 503:
          error.customMessage = 'Error en el servidor. Intenta más tarde.';
          break;
        default:
          error.customMessage = error.response.data?.error || `Error ${error.response.status}`;
      }
    } else if (error.request) {
      error.customMessage = (error.code === 'ECONNABORTED')
        ? 'El servidor tardó demasiado en responder (timeout).'
        : 'Sin respuesta del servidor. Verifica tu conexión.';
    } else {
      error.customMessage = `Error de configuración: ${error.message}`;
    }

    console.error('Error de red:', error.customMessage || error.message);
    return Promise.reject(error);
  }
);

export default axiosInstance;